import { commaSeparatedNumbers } from 'utils/commaSeparatedNumbers';

/**
 * Format date and time for display without timezone conversion
 * @param dateTimeStr ISO date string
 * @returns Formatted date/time string (MM-DD-YYYY HH:MM AM/PM)
 */
export const formatDateTime = (dateTimeStr: string | null | undefined): string => {
  if (!dateTimeStr) {
    return '-';
  }

  try {
    // Instead of creating a Date object which applies timezone conversion,
    // parse the ISO string directly to preserve the exact time
    const parts = dateTimeStr.split('T');
    const datePart = parts[0];
    const timePart = parts[1]?.split('.')[0] || '00:00:00';

    if (!datePart || !timePart) {
      return dateTimeStr;
    }

    // Parse date parts (YYYY-MM-DD)
    const [year, month, day] = datePart.split('-');

    // Parse time parts (HH:MM:SS)
    const [hours24, minutes] = timePart.split(':');

    // Convert 24-hour time to 12-hour format with AM/PM
    const hours24Num = parseInt(hours24);
    const hours12 = hours24Num % 12 || 12; // Convert 0 to 12
    const ampm = hours24Num >= 12 ? 'PM' : 'AM';

    // Format as MM-DD-YYYY HH:MM AM/PM
    return `${month}-${day}-${year} ${hours12}:${minutes} ${ampm}`;
  } catch (error) {
    return dateTimeStr || '-'; // Fallback to raw value
  }
};

/**
 * Format number values with fallback
 * @param value Number value
 * @returns Formatted number or fallback
 */
type NullableNumber = number | null | undefined;
export const formatNumber = (value: NullableNumber): string => {
  if (value === undefined || value === null) {
    return '-';
  }
  return commaSeparatedNumbers(value.toString());
};

/**
 * Format duration in minutes with proper rounding to whole numbers
 * @param minutes Duration in minutes
 * @returns Formatted duration string rounded to whole number
 */
export const formatDurationMinutes = (minutes: number | null | undefined): string => {
  if (minutes === undefined || minutes === null) {
    return '-';
  }
  // Round to nearest whole number
  const roundedMinutes = Math.round(minutes);
  return commaSeparatedNumbers(roundedMinutes.toString());
};

/**
 * Format time values (in minutes) with unit - now properly rounds to whole numbers
 * @param minutes Time in minutes
 * @returns Formatted time string rounded to whole number
 */
export const formatDuration = (minutes: NullableNumber): string => {
  if (minutes === undefined || minutes === null) {
    return '-';
  }
  // Round to nearest whole number
  const roundedMinutes = Math.round(minutes);
  return roundedMinutes.toString();
};

/**
 * Format date only (without time) for display
 * @param dateStr Date string
 * @returns Formatted date string (MM-DD-YYYY)
 */
type NullableString = string | null | undefined;
export const formatDate = (dateStr: NullableString): string => {
  if (!dateStr) {
    return '-';
  }

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return '-';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${month}-${day}-${year}`;
  } catch (error) {
    return dateStr || '-'; // Fallback to raw value
  }
};

/**
 * Format time values with error indication for zero values
 * @param value Time value
 * @returns Formatted time with error indication
 */
export const formatTimeWithError = (value: number | null | undefined): string => {
  if (value === undefined || value === null) {
    return '-';
  }
  if (value === 0) {
    return 'Error';
  }
  return value.toString();
};
